import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useCustomerSelector } from '../../context/CustomerSelectorContext';
import { useAuth } from '../../context/AuthContext';
import { CUSTOMER_TYPE } from '../../constants/customerType';
import legalPersonService from '../../services/legalPersonService';
import physicalPersonService from '../../services/physicalPersonService';
import {
    SelectorContainer,
    SelectorHeader,
    SelectorTitle,
    SelectorContent,
    SelectorOptions,
    OptionButton,
    OptionLabel,
    OptionDescription,
    CustomerSelectorListArea,
    ClientOptionSplit,
    ClientHalfButton,
    SelectButton,
    InfoBadge,
    CollapsedContainer,
    CollapsedContent,
    ChangeButton,
} from './styles';
import { CrudList } from '../shared/Crud';
import { LEGAL_PERSON_LIST_COLUMNS } from '../shared/legalPersonListColumns';
import { PHYSICAL_PERSON_LIST_COLUMNS } from '../shared/physicalPersonListColumns';
import LegalPersonSearchFilter from '../shared/LegalPersonSearchFilter';
import { parseCustomerListSearchQuery } from '../../utils/customerSearchFilter';
import { buildPhysicalPersonListFilters } from '../../utils/physicalPersonListParams';

const BuildingIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 21h18M3 7v14M21 7v14M9 21V7M15 21V7M9 7V3h6v4M9 11h.01M9 15h.01M15 11h.01M15 15h.01" />
    </svg>
);

const UsersIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75M13 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0z" />
    </svg>
);

const InfoIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 16v-4M12 8h.01" />
    </svg>
);

const DEFAULT_FIRST_OPTION_LABEL = 'Minha Empresa';
const DEFAULT_FIRST_OPTION_DESCRIPTION = 'Gerenciar dados da própria empresa';

const CLIENT_KIND = {
    PJ: 'pessoa-juridica',
    PF: 'pessoa-fisica',
};

const SELECTOR_TABLE_MIN_WIDTH = '640px';
const SELECTOR_PAGE_SIZE = 5;
const SELECTOR_SEARCH_HINT = (
    <>
        Com <strong>espaço</strong> ou só letras, filtra por nome; CPF/CNPJ ou CPF (com dígito), por documento.
        Ordenação: nome (A → Z).
    </>
);

const PersonIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" />
    </svg>
);

function clientKindToCustomerType(kind) {
    return kind === CLIENT_KIND.PF ? CUSTOMER_TYPE.PESSOA_FISICA : CUSTOMER_TYPE.PESSOA_JURIDICA;
}

function customerTypeToClientKind(type) {
    return type === CUSTOMER_TYPE.PESSOA_FISICA ? CLIENT_KIND.PF : CLIENT_KIND.PJ;
}

function ClientKindSplit({ value, onSelect, compact = false }) {
    return (
        <ClientOptionSplit
            $compact={compact}
            $active={Boolean(value)}
            role="group"
            aria-label="Tipo de cliente"
        >
            <ClientHalfButton
                type="button"
                $compact={compact}
                $active={value === CLIENT_KIND.PJ}
                onClick={() => onSelect(CLIENT_KIND.PJ)}
            >
                <BuildingIcon />
                <OptionLabel>Pessoa jurídica</OptionLabel>
                <OptionDescription>Empresas e CNPJ</OptionDescription>
            </ClientHalfButton>
            <ClientHalfButton
                type="button"
                $compact={compact}
                $active={value === CLIENT_KIND.PF}
                onClick={() => onSelect(CLIENT_KIND.PF)}
            >
                <PersonIcon />
                <OptionLabel>Pessoa física</OptionLabel>
                <OptionDescription>CPF</OptionDescription>
            </ClientHalfButton>
        </ClientOptionSplit>
    );
}

function normalizePagedResult(result, fallbackPage) {
    const customerList = result.content || [];
    const totalPages = customerList.length === 0 ? 0 : result.page?.totalPages ?? 1;
    const pageNumber =
        totalPages === 0 ? 0 : Math.min(result.page?.number ?? fallbackPage, Math.max(0, totalPages - 1));
    return {
        customerList,
        pageNumber,
        totalPages,
        first: totalPages === 0 || pageNumber === 0,
        last: totalPages === 0 || pageNumber >= totalPages - 1,
    };
}

export default function CustomerSelector({
    firstOptionLabel: firstOptionLabelProp,
    firstOptionDescription: firstOptionDescriptionProp,
    clientOnly = false,
    includePhysicalCustomers = false,
}) {
    useAuth();
    const {
        isManagingOwnData,
        selectedCustomerId,
        selectedCustomerName,
        selectedCustomerType,
        selectOwnData,
        selectCustomerData,
        isSelectorOpen,
        setIsSelectorOpen,
        isTransportCompany,
    } = useCustomerSelector();

    const firstOptionLabel = firstOptionLabelProp?.trim() || DEFAULT_FIRST_OPTION_LABEL;
    const firstOptionDescription = firstOptionDescriptionProp?.trim() || DEFAULT_FIRST_OPTION_DESCRIPTION;

    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [localSearchQuery, setLocalSearchQuery] = useState('');
    const [clientListKind, setClientListKind] = useState(() =>
        selectedCustomerType === CUSTOMER_TYPE.PESSOA_FISICA ? CLIENT_KIND.PF : CLIENT_KIND.PJ,
    );
    const [pagination, setPagination] = useState({
        page: 0,
        totalPages: 0,
        first: true,
        last: true,
    });

    const searchQuery = useDebouncedValue(localSearchQuery, 750);
    const showPhysicalCustomers = includePhysicalCustomers;
    const activeKind = showPhysicalCustomers ? clientListKind : CLIENT_KIND.PJ;

    useEffect(() => {
        if (!showPhysicalCustomers) return;
        if (selectedCustomerType) {
            setClientListKind(customerTypeToClientKind(selectedCustomerType));
        }
    }, [showPhysicalCustomers, selectedCustomerType]);

    const fillViewport =
        isSelectorOpen && (clientOnly || !isManagingOwnData || !selectedCustomerId);

    const enterClientListKind = useCallback(
        (kind) => {
            setClientListKind(kind);
            const type = clientKindToCustomerType(kind);
            if (isManagingOwnData) {
                selectCustomerData(null, '', type);
                return;
            }
            if (selectedCustomerType && selectedCustomerType !== type) {
                selectCustomerData(null, '', type);
            }
        },
        [isManagingOwnData, selectCustomerData, selectedCustomerType],
    );

    const listColumns = useMemo(
        () => (activeKind === 'pessoa-fisica' ? PHYSICAL_PERSON_LIST_COLUMNS : LEGAL_PERSON_LIST_COLUMNS),
        [activeKind],
    );

    useEffect(() => {
        setPagination((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    }, [searchQuery, activeKind]);

    const fetchCustomers = useCallback(async () => {
        setLoading(true);
        try {
            if (activeKind === 'pessoa-fisica') {
                const pfFilters = buildPhysicalPersonListFilters(searchQuery);
                const result = await physicalPersonService.list({
                    page: pagination.page,
                    size: SELECTOR_PAGE_SIZE,
                    sort: 'name,asc',
                    ...pfFilters,
                });
                const { customerList, pageNumber, totalPages, first, last } = normalizePagedResult(
                    result,
                    pagination.page,
                );
                setCustomers(customerList);
                setPagination((prev) => ({
                    ...prev,
                    page: pageNumber,
                    totalPages,
                    first,
                    last,
                }));
                return;
            }

            const { name: nameParam, document: documentParam } = parseCustomerListSearchQuery(searchQuery);
            const result = await legalPersonService.list({
                page: pagination.page,
                size: SELECTOR_PAGE_SIZE,
                sort: 'name,asc',
                ...(nameParam ? { name: nameParam } : {}),
                ...(documentParam ? { document: documentParam } : {}),
            });
            const { customerList, pageNumber, totalPages, first, last } = normalizePagedResult(
                result,
                pagination.page,
            );
            setCustomers(customerList);
            setPagination((prev) => ({
                ...prev,
                page: pageNumber,
                totalPages,
                first,
                last,
            }));
        } catch {
            setCustomers([]);
        } finally {
            setLoading(false);
        }
    }, [activeKind, pagination.page, searchQuery]);

    useEffect(() => {
        if ((clientOnly || !isManagingOwnData) && isSelectorOpen) {
            fetchCustomers();
        }
    }, [clientOnly, isManagingOwnData, isSelectorOpen, fetchCustomers]);

    const handlePageChange = (newPage) => {
        setPagination((prev) => ({ ...prev, page: newPage }));
    };

    const handleSelectCustomer = (customer) => {
        const label = customer?.name ?? (customer?.id != null ? `Cliente #${customer.id}` : 'Cliente');
        const type =
            activeKind === 'pessoa-fisica'
                ? CUSTOMER_TYPE.PESSOA_FISICA
                : CUSTOMER_TYPE.PESSOA_JURIDICA;
        selectCustomerData(customer.id, label, type);
    };

    const isCustomerSelected = (customer) => {
        if (selectedCustomerId !== customer.id) return false;
        const expectedType =
            activeKind === 'pessoa-fisica'
                ? CUSTOMER_TYPE.PESSOA_FISICA
                : CUSTOMER_TYPE.PESSOA_JURIDICA;
        if (showPhysicalCustomers) {
            return selectedCustomerType === expectedType;
        }
        return true;
    };

    const handleSelectOwnCompany = () => {
        selectOwnData();
    };

    const isPhysicalKind = activeKind === 'pessoa-fisica';
    const customerListFilters = (
        <LegalPersonSearchFilter
            idPrefix="customer-selector-search"
            value={localSearchQuery}
            onChange={setLocalSearchQuery}
            placeholder={
                isPhysicalKind ? 'Nome ou CPF' : 'Nome da empresa ou CPF / CNPJ'
            }
            hint={SELECTOR_SEARCH_HINT}
            hintMinHeight={40}
        />
    );

    const showCustomerList = clientOnly || !isManagingOwnData;

    const customerTable = showCustomerList ? (
        <CustomerSelectorListArea $compactTop={clientOnly && showPhysicalCustomers}>
            {clientOnly && showPhysicalCustomers ? (
                <ClientKindSplit
                    compact
                    value={clientListKind}
                    onSelect={enterClientListKind}
                />
            ) : null}
            <CrudList
                title={clientOnly ? '' : 'Selecione um Cliente'}
                tableMinWidth={SELECTOR_TABLE_MIN_WIDTH}
                viewportContained
                columns={listColumns}
                data={customers}
                loading={loading}
                pagination={{
                    ...pagination,
                    onPageChange: handlePageChange,
                }}
                filters={customerListFilters}
                actions={(customer) => (
                    <SelectButton
                        onClick={() => handleSelectCustomer(customer)}
                        $selected={isCustomerSelected(customer)}
                    >
                        {isCustomerSelected(customer) ? 'Selecionado' : 'Selecionar'}
                    </SelectButton>
                )}
            />
        </CustomerSelectorListArea>
    ) : null;

    if (!isTransportCompany) {
        return null;
    }

    if (!clientOnly && !isSelectorOpen && selectedCustomerId) {
        return (
            <CollapsedContainer>
                <CollapsedContent>
                    {isManagingOwnData ? <BuildingIcon /> : <UsersIcon />}
                    <span>Gerenciando:</span>
                    <strong>
                        {selectedCustomerName ||
                            (isManagingOwnData ? firstOptionLabel : 'Cliente Selecionado')}
                    </strong>
                </CollapsedContent>
                <ChangeButton onClick={() => setIsSelectorOpen(true)}>Alterar seleção</ChangeButton>
            </CollapsedContainer>
        );
    }

    if (clientOnly) {
        return (
            <SelectorContainer $fillViewport={fillViewport}>
                <SelectorHeader>
                    <SelectorTitle>
                        <UsersIcon />
                        Selecione o cliente para a entrega
                    </SelectorTitle>
                </SelectorHeader>
                <SelectorContent $fillViewport={fillViewport}>{customerTable}</SelectorContent>
            </SelectorContainer>
        );
    }

    return (
        <SelectorContainer $fillViewport={fillViewport}>
            <SelectorHeader>
                <SelectorTitle>
                    <BuildingIcon />
                    Gerenciar Dados
                </SelectorTitle>
            </SelectorHeader>

            <SelectorContent $fillViewport={fillViewport}>
                <SelectorOptions>
                    <OptionButton $active={isManagingOwnData} onClick={handleSelectOwnCompany} type="button">
                        <BuildingIcon />
                        <OptionLabel>{firstOptionLabel}</OptionLabel>
                        <OptionDescription>{firstOptionDescription}</OptionDescription>
                    </OptionButton>

                    {showPhysicalCustomers ? (
                        <ClientKindSplit
                            value={!isManagingOwnData ? clientListKind : null}
                            onSelect={enterClientListKind}
                        />
                    ) : (
                        <OptionButton
                            $active={!isManagingOwnData}
                            onClick={() => {
                                if (isManagingOwnData) {
                                    selectCustomerData(null, '', CUSTOMER_TYPE.PESSOA_JURIDICA);
                                }
                            }}
                            type="button"
                        >
                            <UsersIcon />
                            <OptionLabel>Cliente</OptionLabel>
                            <OptionDescription>Gerenciar dados de um cliente específico</OptionDescription>
                        </OptionButton>
                    )}
                </SelectorOptions>

                {customerTable}

                {isManagingOwnData && (
                    <InfoBadge>
                        <InfoIcon />
                        <span>Você está gerenciando os dados da sua empresa de transporte</span>
                    </InfoBadge>
                )}

                {!isManagingOwnData && selectedCustomerId && (
                    <InfoBadge>
                        <InfoIcon />
                        <span>Você está gerenciando os dados do cliente selecionado</span>
                    </InfoBadge>
                )}
            </SelectorContent>
        </SelectorContainer>
    );
}
